import * as React from 'react'
import {
  ArrowLeft,
  Bell,
  Check,
  ChevronRight,
  Clock3,
  Edit,
  Filter,
  Folder,
  FolderInput,
  FolderPlus,
  Globe,
  Hash,
  LayoutGrid,
  List,
  MessageCircle,
  Phone,
  Plus,
  Power,
  PowerOff,
  Search,
  Settings,
  Shield,
  Star,
  Trash2,
  User,
  Users,
  X,
  Zap,
} from 'lucide-react'
import { Button } from '@/components/ui/button'
import { Input } from '@/components/ui/input'
import { Badge } from '@/components/ui/badge'
import { Skeleton } from '@/components/ui/skeleton'
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from '@/components/ui/select'
import { cn } from '@/lib/utils'
import { useIsMobile } from '@/hooks/use-mobile'
import type { StoredToolIcon } from '@/lib/tool-icons'
import type { MCPAction } from '@/types/tool'
import type { ToolFolder, ToolPlacement } from '@/types/tool-taxonomy'
import { TarjetaArrastrable } from '@/components/ui/tarjeta-arrastrable'
import {
  AlertDialog,
  AlertDialogAction,
  AlertDialogCancel,
  AlertDialogContent,
  AlertDialogDescription,
  AlertDialogFooter,
  AlertDialogHeader,
  AlertDialogTitle,
} from '@/components/ui/alert-dialog'

type ViewMode = 'grid' | 'list'

interface ToolsExplorerContentManagerProps {
  tools: MCPAction[]
  folders: ToolFolder[]
  placements: ToolPlacement[]
  searchQuery: string
  onSearchQueryChange: (value: string) => void
  typeFilter: string
  onTypeFilterChange: (value: string) => void
  enabledFilter: string
  onEnabledFilterChange: (value: string) => void
  loading?: boolean
  onCreateFolder: (payload: { name: string; parent_id: string | null }) => Promise<void>
  onMoveFolder?: (folderId: string, parentId: string | null) => Promise<void>
  onDeleteFolder?: (folderId: string) => Promise<void>
  onMoveTool: (toolId: string, folderId: string | null) => Promise<void>
  onBulkMoveTools?: (toolIds: string[], folderId: string | null) => Promise<void>
  onEditTool: (tool: MCPAction) => void
  onDeleteTool: (tool: MCPAction) => void
  onDeleteManyTools?: (toolIds: string[]) => Promise<void>
  onToggleTool: (tool: MCPAction) => void
  onCreateTool?: () => void
  toolIcons?: Record<string, StoredToolIcon>
  viewMode?: ViewMode
  onViewModeChange?: (mode: ViewMode) => void
  showCreateFolder?: boolean
  onShowCreateFolderChange?: (open: boolean) => void
  hideMobileToolbar?: boolean
  currentFolderId?: string | null
  onCurrentFolderIdChange?: (folderId: string | null) => void
}

function formatRelativeTime(value?: string): string {
  if (!value) return '-'
  const date = new Date(value)
  const ts = date.getTime()
  if (!Number.isFinite(ts)) return '-'
  const diffMs = Date.now() - ts
  const diffHours = Math.max(1, Math.floor(diffMs / (1000 * 60 * 60)))
  if (diffHours < 24) return `hace ${diffHours}h`
  const diffDays = Math.floor(diffHours / 24)
  if (diffDays < 30) return `hace ${diffDays}d`
  const diffMonths = Math.floor(diffDays / 30)
  return `hace ${diffMonths}m`
}

function getExecutionTypeLabel(type: string) {
  if (type === 'python') return 'Webhook / HTTP Request'
  if (type === 'webhook') return 'Webhook / HTTP Request'
  return 'MCP Server'
}

// El tipo ya lo dice la etiqueta -«Webhook / HTTP Request», «MCP Server»-, asi
// que el color no anadia nada: de hecho `webhook` y `python` devolvian las
// MISMAS clases, y nadie lo vio porque un color que no distingue no se nota.
function typeChipClasses(): string {
  return 'border bg-muted text-muted-foreground'
}

function WebhookMonoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path d="M18 16.98h-5.99c-1.1 0-1.95.94-2.48 1.9A4 4 0 0 1 2 17c.01-.7.2-1.4.57-2" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m6 17 3.13-5.78c.53-.97.1-2.18-.5-3.1a4 4 0 1 1 6.89-4.06" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
      <path d="m12 6 3.13 5.73C15.66 12.7 16.9 13 18 13a4 4 0 0 1 0 8" stroke="currentColor" strokeWidth="2" strokeLinecap="round" strokeLinejoin="round" />
    </svg>
  )
}

function McpMonoIcon({ className }: { className?: string }) {
  return (
    <svg viewBox="0 0 24 24" className={className} fill="none" xmlns="http://www.w3.org/2000/svg" aria-hidden="true">
      <path
        fill="currentColor"
        d="M13.85 0a4.16 4.16 0 0 0-2.95 1.217L1.456 10.66a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l9.442-9.442a2.49 2.49 0 0 1 3.541 0 2.49 2.49 0 0 1 0 3.541L8.59 12.97l-.1.1a.835.835 0 0 0 0 1.18.835.835 0 0 0 1.18 0l.1-.098 7.03-7.034a2.49 2.49 0 0 1 3.542 0l.049.05a2.49 2.49 0 0 1 0 3.54l-8.54 8.54a1.96 1.96 0 0 0 0 2.755l1.753 1.753a.835.835 0 0 0 1.18 0 .835.835 0 0 0 0-1.18l-1.753-1.753a.266.266 0 0 1 0-.394l8.54-8.54a4.185 4.185 0 0 0 0-5.9l-.05-.05a4.16 4.16 0 0 0-2.95-1.218c-.2 0-.401.02-.6.048a4.17 4.17 0 0 0-1.17-3.552A4.16 4.16 0 0 0 13.85 0m0 3.333a.84.84 0 0 0-.59.245L6.275 10.56a4.186 4.186 0 0 0 0 5.902 4.186 4.186 0 0 0 5.902 0L19.16 9.48a.835.835 0 0 0 0-1.18.835.835 0 0 0-1.18 0l-6.985 6.984a2.49 2.49 0 0 1-3.54 0 2.49 2.49 0 0 1 0-3.54l6.983-6.985a.835.835 0 0 0 0-1.18.84.84 0 0 0-.59-.245"
      />
    </svg>
  )
}

function ExecutionTypeIcon({ type, className }: { type: string; className?: string }) {
  if (type === 'webhook') return <WebhookMonoIcon className={className} />
  if (type === 'python') return <WebhookMonoIcon className={className} />
  return <McpMonoIcon className={className} />
}

function ExecutionTypeChip({ type }: { type: string }) {
  return (
    <span className={cn('inline-flex items-center rounded-full border px-2 py-0.5 text-xs font-semibold', typeChipClasses())}>
      {getExecutionTypeLabel(type)}
    </span>
  )
}

function getMcpRemoteSummary(tool: MCPAction): { enabled: number; total: number } | null {
  if (tool.execution_type !== 'mcp_server') return null
  const external = tool.http_config?.external_mcp
  if (!external) return null
  const catalog = Array.isArray(external.remote_tools_catalog) ? external.remote_tools_catalog : []
  const enabled = Array.isArray(external.enabled_remote_tools) ? external.enabled_remote_tools : []
  return { enabled: enabled.length, total: catalog.length }
}

const CUSTOM_ICON_MAP = {
  filter: Filter,
  phone: Phone,
  globe: Globe,
  star: Star,
  zap: Zap,
  bell: Bell,
  message: MessageCircle,
  'message-circle': MessageCircle,
  user: User,
  users: Users,
  hash: Hash,
  check: Check,
  search: Search,
  settings: Settings,
  shield: Shield,
} as const

function StoredIcon({
  value,
  colorClass,
  className,
}: {
  value?: string
  colorClass?: string
  className?: string
}) {
  if (!value) return null
  if (value.startsWith('emoji:')) {
    return <span className={cn('leading-none', className)}>{value.replace('emoji:', '')}</span>
  }
  const IconComponent = CUSTOM_ICON_MAP[value as keyof typeof CUSTOM_ICON_MAP] ?? Filter
  return <IconComponent className={cn(className, colorClass)} />
}

function ToolFolderPreview({
  childFolders,
  childTools,
  toolIcons,
}: {
  childFolders: ToolFolder[]
  childTools: MCPAction[]
  toolIcons: Record<string, StoredToolIcon>
}) {
  const items = [
    ...childFolders.map((folder) => ({ id: `folder-${folder.id}`, kind: 'folder' as const, label: folder.name })),
    ...childTools.map((tool) => ({
      id: `tool-${tool.id}`,
      kind: 'tool' as const,
      label: tool.display_name,
      type: tool.execution_type,
      icon: toolIcons[tool.id],
    })),
  ]
  // Keep 5 visible tiles to preserve readable masonry proportions and avoid
  // icon/title overlap in very short cells.
  const visible = items.slice(0, 5)
  const extra = Math.max(0, items.length - visible.length)

  if (items.length === 0) {
    return (
      <div className="h-24 flex items-center justify-center bg-muted/10 dark:bg-muted/20">
        <Folder className="h-6 w-6 text-muted-foreground/45 dark:text-muted-foreground/30" />
      </div>
    )
  }

  const getMasonryTemplate = (
    count: number
  ): { rows: string[]; areas: string[] } => {
    if (count <= 1) {
      return {
        rows: [
          'a a a a a a',
          'a a a a a a',
          'a a a a a a',
          'a a a a a a',
        ],
        areas: ['a'],
      }
    }
    if (count === 2) {
      return {
        rows: [
          'a a a a b b',
          'a a a a b b',
          'a a a a b b',
          'a a a a b b',
        ],
        areas: ['a', 'b'],
      }
    }
    if (count === 3) {
      return {
        rows: [
          'a a a b b b',
          'a a a b b b',
          'a a a c c c',
          'a a a c c c',
        ],
        areas: ['a', 'b', 'c'],
      }
    }
    if (count === 4) {
      return {
        rows: [
          'a a a b b b',
          'a a a b b b',
          'c c d d d d',
          'c c d d d d',
        ],
        areas: ['a', 'b', 'c', 'd'],
      }
    }
    if (count === 5) {
      return {
        rows: [
          'a a b b c c',
          'a a b b c c',
          'd d d e e e',
          'd d d e e e',
        ],
        areas: ['a', 'b', 'c', 'd', 'e'],
      }
    }
    return {
      rows: [
        'a a b b c c',
        'a a d d c c',
        'e e d d f f',
        'e e d d f f',
      ],
      areas: ['a', 'b', 'c', 'd', 'e', 'f'],
    }
  }

  const masonry = getMasonryTemplate(visible.length)

  return (
    <div
      className="relative grid h-24 grid-cols-6 grid-rows-4 gap-0.5 overflow-hidden bg-muted/10 p-0.5 dark:bg-muted/20"
      style={{ gridTemplateAreas: masonry.rows.map((row) => `"${row}"`).join(' ') }}
    >
      {visible.map((item, index) => (
        <div
          key={item.id}
          className="relative overflow-hidden rounded-[6px] border bg-background/95 dark:bg-card/70"
          style={{ gridArea: masonry.areas[index] }}
        >
          <div className="absolute left-1.5 top-1.5 inline-flex items-center gap-1">
            {item.kind === 'folder' ? (
              <Folder className="h-3 w-3 text-muted-foreground" />
            ) : (
              item.icon?.icon ? (
                <StoredIcon
                  value={item.icon?.icon}
                  colorClass={item.icon?.colorClass}
                  className="h-3 w-3 text-foreground"
                />
              ) : (
                <ExecutionTypeIcon type={item.type} className="h-3 w-3 text-foreground" />
              )
            )}
          </div>
          <div className="absolute inset-x-0 bottom-0 flex items-center justify-between gap-1 bg-background/90 px-1.5 py-1 dark:bg-black/45">
            <span className="truncate text-xs font-medium text-foreground/90 dark:text-white/95">{item.label}</span>
            {item.kind === 'tool' ? (
              <span className="inline-flex shrink-0 items-center rounded-full border bg-muted/40 px-1.5 py-0.5 text-xs font-semibold text-foreground/85 dark:border-white/25 dark:bg-transparent dark:text-white/95">
                {item.type === 'webhook' || item.type === 'python' ? 'WEB' : 'MCP'}
              </span>
            ) : (
              <span className="inline-flex shrink-0 items-center rounded-full border bg-muted/40 px-1.5 py-0.5 text-xs font-semibold text-foreground/85 dark:border-white/25 dark:bg-transparent dark:text-white/95">
                DIR
              </span>
            )}
          </div>
        </div>
      ))}
      {extra > 0 ? (
        <div className="absolute inset-0 flex items-center justify-center bg-background/70 dark:bg-black/35">
          <span className="rounded-full border bg-background/95 px-2 py-1 text-xs font-semibold text-foreground dark:border-white/25 dark:bg-black/60 dark:text-white">+{extra}</span>
        </div>
      ) : null}
    </div>
  )
}

function ToolFolderInlinePreview({
  childFolders,
  childTools,
  toolIcons,
}: {
  childFolders: ToolFolder[]
  childTools: MCPAction[]
  toolIcons: Record<string, StoredToolIcon>
}) {
  const items = [
    ...childFolders.map((folder) => ({ id: `folder-${folder.id}`, kind: 'folder' as const, label: folder.name })),
    ...childTools.map((tool) => ({
      id: `tool-${tool.id}`,
      kind: 'tool' as const,
      label: tool.display_name,
      type: tool.execution_type,
      icon: toolIcons[tool.id],
    })),
  ].slice(0, 4)

  if (!items.length) {
    return (
      <div className="inline-flex h-9 items-center rounded-md border border-dashed border-border px-2 text-xs text-muted-foreground">
        Vacia
      </div>
    )
  }

  return (
    <div className="flex min-w-0 flex-1 items-center gap-1.5 overflow-hidden sm:max-w-[420px]">
      {items.map((item) => (
        <div
          key={item.id}
          className="inline-flex min-w-[88px] max-w-[120px] items-center gap-1.5 rounded-md border bg-card/70 px-2 py-1"
        >
          {item.kind === 'folder' ? (
            <Folder className="h-3.5 w-3.5 shrink-0 text-muted-foreground" />
          ) : (
            item.icon?.icon ? (
              <StoredIcon
                value={item.icon?.icon}
                colorClass={item.icon?.colorClass}
                className="h-3.5 w-3.5 shrink-0 text-foreground"
              />
            ) : (
              <ExecutionTypeIcon type={item.type} className="h-3.5 w-3.5 shrink-0 text-foreground" />
            )
          )}
          <span className="truncate text-xs text-foreground/90">{item.label}</span>
        </div>
      ))}
    </div>
  )
}

export function ToolsExplorerContentManager({
  tools,
  folders,
  placements,
  searchQuery,
  onSearchQueryChange,
  typeFilter,
  onTypeFilterChange,
  enabledFilter,
  onEnabledFilterChange,
  loading = false,
  onCreateFolder,
  onMoveFolder,
  onDeleteFolder,
  onMoveTool,
  onBulkMoveTools,
  onEditTool,
  onDeleteTool,
  onDeleteManyTools,
  onToggleTool,
  onCreateTool,
  toolIcons = {},
  viewMode: controlledViewMode,
  onViewModeChange,
  showCreateFolder: controlledShowCreateFolder,
  onShowCreateFolderChange,
  hideMobileToolbar = false,
  currentFolderId: controlledCurrentFolderId,
  onCurrentFolderIdChange,
}: ToolsExplorerContentManagerProps) {
  const isMobile = useIsMobile()
  const [localCurrentFolderId, setLocalCurrentFolderId] = React.useState<string | null>(null)
  const [localViewMode, setLocalViewMode] = React.useState<ViewMode>('grid')
  const [localShowCreateFolder, setLocalShowCreateFolder] = React.useState(false)
  const currentFolderId = controlledCurrentFolderId ?? localCurrentFolderId
  const viewMode = controlledViewMode ?? localViewMode
  const showCreateFolder = controlledShowCreateFolder ?? localShowCreateFolder
  const setCurrentFolderId = React.useCallback((value: React.SetStateAction<string | null>) => {
    const next = typeof value === 'function' ? value(currentFolderId) : value
    if (onCurrentFolderIdChange) {
      onCurrentFolderIdChange(next)
      return
    }
    setLocalCurrentFolderId(next)
  }, [currentFolderId, onCurrentFolderIdChange])
  const setViewMode = React.useCallback((mode: ViewMode) => {
    if (onViewModeChange) {
      onViewModeChange(mode)
      return
    }
    setLocalViewMode(mode)
  }, [onViewModeChange])
  const setShowCreateFolder = React.useCallback((value: React.SetStateAction<boolean>) => {
    const next = typeof value === 'function' ? value(showCreateFolder) : value
    if (onShowCreateFolderChange) {
      onShowCreateFolderChange(next)
      return
    }
    setLocalShowCreateFolder(next)
  }, [onShowCreateFolderChange, showCreateFolder])
  const [newFolderName, setNewFolderName] = React.useState('')
  const [savingFolder, setSavingFolder] = React.useState(false)
  const [selectedToolIds, setSelectedToolIds] = React.useState<Set<string>>(new Set())
  const [selectedFolderIds, setSelectedFolderIds] = React.useState<Set<string>>(new Set())
  const [draggingToolIds, setDraggingToolIds] = React.useState<Set<string>>(new Set())
  const [draggingFolderIds, setDraggingFolderIds] = React.useState<Set<string>>(new Set())
  const [dropTargetFolderId, setDropTargetFolderId] = React.useState<string | null>(null)
  const [bulkMoving, setBulkMoving] = React.useState(false)
  const [bulkMovePickerKey, setBulkMovePickerKey] = React.useState(0)
  const [bulkMoveError, setBulkMoveError] = React.useState<string | null>(null)
  const [rubberBand, setRubberBand] = React.useState<{
    startX: number
    startY: number
    x: number
    y: number
  } | null>(null)
  const surfaceRef = React.useRef<HTMLDivElement | null>(null)
  const toolCardRefs = React.useRef<Map<string, HTMLElement>>(new Map())
  const folderCardRefs = React.useRef<Map<string, HTMLElement>>(new Map())
  const dragGhostRef = React.useRef<HTMLDivElement | null>(null)
  const moveInFlightRef = React.useRef(false)

  const folderById = React.useMemo(() => {
    return new Map(folders.map((folder) => [folder.id, folder]))
  }, [folders])

  const placementByToolId = React.useMemo(() => {
    return new Map(placements.map((placement) => [placement.tool_id, placement]))
  }, [placements])

  const breadcrumbs = React.useMemo(() => {
    const chain: ToolFolder[] = []
    let cursor = currentFolderId ? folderById.get(currentFolderId) : undefined
    while (cursor) {
      chain.unshift(cursor)
      cursor = cursor.parent_id ? folderById.get(cursor.parent_id) : undefined
    }
    return chain
  }, [currentFolderId, folderById])

  const visibleFolders = React.useMemo(() => {
    return folders.filter((folder) => folder.parent_id === currentFolderId)
  }, [folders, currentFolderId])

  const visibleTools = React.useMemo(() => {
    return tools.filter((tool) => {
      const placement = placementByToolId.get(tool.id)
      const folderId = placement?.folder_id ?? null
      return folderId === currentFolderId
    })
  }, [tools, placementByToolId, currentFolderId])

  const getFolderPreviewData = React.useCallback((folderId: string) => {
    const childFolders = folders.filter((folder) => folder.parent_id === folderId)
    const childTools = tools.filter((tool) => {
      const placement = placementByToolId.get(tool.id)
      return (placement?.folder_id ?? null) === folderId
    })
    return {
      total: childFolders.length + childTools.length,
      childFolders,
      childTools,
    }
  }, [folders, tools, placementByToolId])

  const isDescendantFolder = React.useCallback((folderId: string, possibleParentId: string): boolean => {
    let cursor: string | null = possibleParentId
    while (cursor) {
      if (cursor === folderId) return true
      const folder = folderById.get(cursor)
      cursor = folder?.parent_id ?? null
    }
    return false
  }, [folderById])

  React.useEffect(() => {
    const availableIds = new Set(visibleTools.map((tool) => tool.id))
    setSelectedToolIds((prev) => new Set(Array.from(prev).filter((id) => availableIds.has(id))))
  }, [visibleTools])

  React.useEffect(() => {
    const availableIds = new Set(visibleFolders.map((folder) => folder.id))
    setSelectedFolderIds((prev) => new Set(Array.from(prev).filter((id) => availableIds.has(id))))
  }, [visibleFolders])

  const handleCreateFolder = async () => {
    if (!newFolderName.trim()) return
    try {
      setSavingFolder(true)
      await onCreateFolder({ name: newFolderName.trim(), parent_id: currentFolderId })
      setNewFolderName('')
      setShowCreateFolder(false)
    } finally {
      setSavingFolder(false)
    }
  }

  const clearSelection = () => {
    setSelectedToolIds(new Set())
    setSelectedFolderIds(new Set())
  }

  const handleToolCardClick = (event: React.MouseEvent, tool: MCPAction) => {
    if (!event.metaKey && !event.ctrlKey && !event.shiftKey) {
      onEditTool(tool)
      return
    }
    const toolId = tool.id
    const isMulti = event.metaKey || event.ctrlKey || event.shiftKey
    setSelectedToolIds((prev) => {
      if (isMulti) {
        const next = new Set(prev)
        if (next.has(toolId)) next.delete(toolId)
        else next.add(toolId)
        return next
      }
      if (prev.size === 1 && prev.has(toolId)) return new Set()
      return new Set([toolId])
    })
  }

  const handleFolderCardClick = (event: React.MouseEvent, folderId: string) => {
    const isMulti = event.metaKey || event.ctrlKey || event.shiftKey
    if (!isMulti) {
      setCurrentFolderId(folderId)
      return
    }
    setSelectedFolderIds((prev) => {
      const next = new Set(prev)
      if (next.has(folderId)) next.delete(folderId)
      else next.add(folderId)
      return next
    })
  }

  const moveToolIdsTo = async (toolIds: string[], folderId: string | null) => {
    if (!toolIds.length) return
    const uniqueToolIds = Array.from(new Set(toolIds))
    if (onBulkMoveTools) {
      try {
        await onBulkMoveTools(uniqueToolIds, folderId)
      } catch {
        // Fallback to individual moves to avoid partial UX failures.
      }
    }
    // Always apply per-tool moves as the source of truth for visible state.
    await Promise.all(uniqueToolIds.map((toolId) => onMoveTool(toolId, folderId)))
  }

  const moveSelectedItems = async (folderId: string | null) => {
    if (moveInFlightRef.current) return
    const selectedTools = Array.from(selectedToolIds)
    const selectedFolders = Array.from(selectedFolderIds)
    const movableToolIds = selectedTools.filter((toolId) => {
      const currentFolderId = placementByToolId.get(toolId)?.folder_id ?? null
      return currentFolderId !== folderId
    })
    if (!selectedTools.length && !selectedFolders.length) return
    try {
      moveInFlightRef.current = true
      setBulkMoving(true)
      setBulkMoveError(null)
      if (selectedFolders.length && onMoveFolder) {
        const movableFolderIds = selectedFolders.filter((candidateId) => {
          if (folderId === null) return true
          if (candidateId === folderId) return false
          return !isDescendantFolder(candidateId, folderId)
        })
        await Promise.all(movableFolderIds.map((candidateId) => onMoveFolder(candidateId, folderId)))
        if (movableFolderIds.length !== selectedFolders.length) {
          setBulkMoveError('Algunas carpetas no se pudieron mover por conflicto de jerarquía.')
        }
      }
      if (selectedTools.length && !movableToolIds.length && !selectedFolders.length) {
        setBulkMoveError('Los elementos seleccionados ya están en esa carpeta.')
        return
      }
      if (movableToolIds.length) {
        await moveToolIdsTo(movableToolIds, folderId)
      }
      clearSelection()
    } catch (error) {
      console.error('Error moving selected items:', error)
      setBulkMoveError('No se pudieron mover los elementos seleccionados.')
    } finally {
      setBulkMoving(false)
      moveInFlightRef.current = false
    }
  }

  const handleDragStart = (event: React.DragEvent, toolId: string) => {
    const dragIds = selectedToolIds.has(toolId) && selectedToolIds.size > 1
      ? new Set(selectedToolIds)
      : new Set([toolId])
    setDraggingToolIds(dragIds)
    setDraggingFolderIds(new Set())
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/tool-ids', JSON.stringify(Array.from(dragIds)))

    const cards: HTMLElement[] = []
    Array.from(dragIds).forEach((id) => {
      const card = toolCardRefs.current.get(id)
      if (card && cards.length < 3) cards.push(card)
    })
    if (!cards.length) return

    const count = dragIds.size
    const first = cards[0]
    const cardW = Math.min(first.offsetWidth, 240)
    const cardH = Math.min(first.offsetHeight, 150)
    const stackOffset = 8
    const totalW = cardW + stackOffset * (cards.length - 1) + 32
    const totalH = cardH + stackOffset * (cards.length - 1) + 16

    const ghost = document.createElement('div')
    ghost.style.cssText = `position:fixed;top:-2000px;left:-2000px;pointer-events:none;z-index:99999;width:${totalW}px;height:${totalH}px;`
    const stack = document.createElement('div')
    stack.style.cssText = `position:relative;width:${totalW}px;height:${totalH}px;`
    // Sin clases: esto se crea con el DOM. Los tokens siguen valiendo escritos
    // como `hsl(var(--x))`, y asi el fantasma cambia con el tema igual que la
    // tarjeta de la que sale -antes llevaba dos hex y un modo oscuro a mano-.
    const bg = 'hsl(var(--card))'
    const border = 'hsl(var(--border))'

    const clones = [...cards].reverse()
    clones.forEach((original, reverseIdx) => {
      const idx = cards.length - 1 - reverseIdx
      const clone = original.cloneNode(true) as HTMLElement
      const offset = idx * stackOffset
      clone.style.cssText = [
        `position:absolute;top:${offset}px;left:${offset}px;`,
        `width:${cardW}px;height:${cardH}px;`,
        'transform:scale(0.95);',
        'border-radius:12px;overflow:hidden;',
        `background:${bg};`,
        `border:1px solid ${border};`,
        'box-shadow:0 12px 32px rgba(0,0,0,.35), 0 2px 8px rgba(0,0,0,.2);',
        `opacity:${idx === 0 ? 1 : 0.9};`,
        'pointer-events:none;',
      ].join('')
      clone.classList.remove('border-primary', 'ring-2', 'ring-primary')
      stack.appendChild(clone)
    })

    if (count > 1) {
      const badge = document.createElement('div')
      badge.style.cssText = `
        position:absolute; top:-4px; right:10px; z-index:12;
        min-width:24px; height:24px; border-radius:999px; padding:0 7px;
        background:#334155; color:white; font-size:12px; font-weight:700;
        display:flex; align-items:center; justify-content:center;
        box-shadow:0 2px 8px rgba(0,0,0,.35);
      `
      badge.textContent = String(count)
      stack.appendChild(badge)
    }

    ghost.appendChild(stack)
    document.body.appendChild(ghost)
    dragGhostRef.current = ghost
    event.dataTransfer.setDragImage(ghost, Math.round(cardW / 2), Math.round(cardH / 2))

    requestAnimationFrame(() => {
      setTimeout(() => {
        // Remove this call's own ghost: a second dragstart may have already
        // replaced the ref, and clearing the ref instead would orphan this node.
        try { ghost.remove() } catch {
          // Ignore cleanup race conditions.
        }
        if (dragGhostRef.current === ghost) {
          dragGhostRef.current = null
        }
      }, 0)
    })
  }

  const handleDragEnd = () => {
    setDraggingToolIds(new Set())
    setDraggingFolderIds(new Set())
    setDropTargetFolderId(null)
    if (dragGhostRef.current) {
      try { document.body.removeChild(dragGhostRef.current) } catch {
        // Ignore cleanup race conditions.
      }
      dragGhostRef.current = null
    }
  }

  const handleFolderDragOver = (event: React.DragEvent, folderId: string) => {
    if (draggingToolIds.size === 0 && draggingFolderIds.size === 0) return
    event.preventDefault()
    event.dataTransfer.dropEffect = 'move'
    setDropTargetFolderId(folderId)
  }

  const handleFolderDrop = async (event: React.DragEvent, folderId: string) => {
    event.preventDefault()
    if (draggingFolderIds.size > 0) {
      if (onMoveFolder) {
        const folderIds = Array.from(draggingFolderIds).filter((candidateId) => {
          if (candidateId === folderId) return false
          return !isDescendantFolder(candidateId, folderId)
        })
        await Promise.all(folderIds.map((candidateId) => onMoveFolder(candidateId, folderId)))
      }
      setDraggingFolderIds(new Set())
      setDropTargetFolderId(null)
      return
    }
    const dragIds = Array.from(draggingToolIds)
    if (!dragIds.length) return
    await moveToolIdsTo(dragIds, folderId)
    setDraggingToolIds(new Set())
    setDropTargetFolderId(null)
    clearSelection()
  }

  const handleFolderDragStart = (event: React.DragEvent, folderId: string) => {
    const dragIds = selectedFolderIds.has(folderId) && selectedFolderIds.size > 1
      ? new Set(selectedFolderIds)
      : new Set([folderId])
    setDraggingFolderIds(dragIds)
    setDraggingToolIds(new Set())
    event.dataTransfer.effectAllowed = 'move'
    event.dataTransfer.setData('text/folder-ids', JSON.stringify(Array.from(dragIds)))
  }

  const handleRbMouseDown = (event: React.MouseEvent<HTMLDivElement>) => {
    if (event.button !== 0) return
    const target = event.target as HTMLElement
    if (
      target.closest('[data-tool-card]') ||
      target.closest('[data-folder-card]') ||
      target.closest('[data-no-rubberband]') ||
      target.closest('button') ||
      target.closest('input') ||
      target.closest('textarea') ||
      target.closest('select') ||
      target.closest('[role="combobox"]') ||
      target.closest('[data-radix-select-trigger]')
    ) {
      return
    }
    event.preventDefault()
    setRubberBand({
      startX: event.clientX,
      startY: event.clientY,
      x: event.clientX,
      y: event.clientY,
    })
    clearSelection()
  }

  React.useEffect(() => {
    if (!rubberBand) return

    const onMouseMove = (event: MouseEvent) => {
      setRubberBand((prev) => (prev ? { ...prev, x: event.clientX, y: event.clientY } : null))
    }

    const onMouseUp = () => {
      setRubberBand(null)
    }

    window.addEventListener('mousemove', onMouseMove)
    window.addEventListener('mouseup', onMouseUp)
    return () => {
      window.removeEventListener('mousemove', onMouseMove)
      window.removeEventListener('mouseup', onMouseUp)
    }
  }, [rubberBand])

  React.useEffect(() => {
    if (!rubberBand) return
    const minX = Math.min(rubberBand.startX, rubberBand.x)
    const maxX = Math.max(rubberBand.startX, rubberBand.x)
    const minY = Math.min(rubberBand.startY, rubberBand.y)
    const maxY = Math.max(rubberBand.startY, rubberBand.y)

    const nextTools = new Set<string>()
    const nextFolders = new Set<string>()
    visibleTools.forEach((tool) => {
      const element = toolCardRefs.current.get(tool.id)
      if (!element) return
      const rect = element.getBoundingClientRect()
      const intersects = rect.left <= maxX && rect.right >= minX && rect.top <= maxY && rect.bottom >= minY
      if (intersects) nextTools.add(tool.id)
    })
    visibleFolders.forEach((folder) => {
      const element = folderCardRefs.current.get(folder.id)
      if (!element) return
      const rect = element.getBoundingClientRect()
      const intersects = rect.left <= maxX && rect.right >= minX && rect.top <= maxY && rect.bottom >= minY
      if (intersects) nextFolders.add(folder.id)
    })
    setSelectedToolIds(nextTools)
    setSelectedFolderIds(nextFolders)
  }, [rubberBand, visibleTools, visibleFolders])

  const renderFolderCard = (folder: ToolFolder) => {
    const isDropTarget = dropTargetFolderId === folder.id
    const isSelected = selectedFolderIds.has(folder.id)
    const preview = getFolderPreviewData(folder.id)

    if (viewMode === 'list') {
      return (
        <TarjetaArrastrable
          key={folder.id}
          onClick={(event) => handleFolderCardClick(event, folder.id)}
          onDragStart={(event) => handleFolderDragStart(event, folder.id)}
          onDragEnd={handleDragEnd}
          onDragOver={(event) => handleFolderDragOver(event, folder.id)}
          onDragLeave={() => setDropTargetFolderId(null)}
          onDrop={(event) => void handleFolderDrop(event, folder.id)}
          data-folder-card
          destinoDeSoltar={isDropTarget}
          elegida={isSelected}
          ref={(node) => {
            if (node) folderCardRefs.current.set(folder.id, node)
            else folderCardRefs.current.delete(folder.id)
          }}
          className="flex items-center justify-between px-4 py-3"
        >
          {/* Los min-w-[Npx] fijaban un suelo (180+78+370) mayor que el viewport
              movil: se aplican solo desde sm. */}
          <div className="flex min-w-0 flex-1 items-center gap-2">
            <span className="inline-flex min-w-0 items-center gap-2 truncate font-medium sm:min-w-[180px]">
              <Folder className="h-4 w-4 shrink-0 text-primary" />
              <span className="truncate">{folder.name}</span>
            </span>
            <p className="shrink-0 whitespace-nowrap text-xs text-muted-foreground sm:min-w-[78px]">{preview.total} elementos</p>
            <ToolFolderInlinePreview childFolders={preview.childFolders} childTools={preview.childTools} toolIcons={toolIcons} />
          </div>
          <ChevronRight className="h-4 w-4 shrink-0 text-muted-foreground" />
        </TarjetaArrastrable>
      )
    }

    return (
      <TarjetaArrastrable
        key={folder.id}
        onClick={(event) => handleFolderCardClick(event, folder.id)}
        onDragStart={(event) => handleFolderDragStart(event, folder.id)}
        onDragEnd={handleDragEnd}
        onDragOver={(event) => handleFolderDragOver(event, folder.id)}
        onDragLeave={() => setDropTargetFolderId(null)}
        onDrop={(event) => void handleFolderDrop(event, folder.id)}
        data-folder-card
        destinoDeSoltar={isDropTarget}
        elegida={isSelected}
        ref={(node) => {
          if (node) folderCardRefs.current.set(folder.id, node)
          else folderCardRefs.current.delete(folder.id)
        }}
        className="overflow-hidden"
      >
        <ToolFolderPreview
          childFolders={preview.childFolders}
          childTools={preview.childTools}
          toolIcons={toolIcons}
        />
        <div className="flex items-center justify-between px-3 py-2.5 border-t border-border">
          <div className="min-w-0">
            <p className="inline-flex min-w-0 max-w-full items-center gap-1.5 text-sm font-medium">
              <Folder className="h-3.5 w-3.5 shrink-0 text-primary" />
              <span className="truncate">{folder.name}</span>
            </p>
            <p className="text-xs text-muted-foreground">
              {preview.total} elementos
            </p>
          </div>
          <ChevronRight className="h-4 w-4 text-muted-foreground" />
        </div>
      </TarjetaArrastrable>
    )
  }

  const renderToolActions = (tool: MCPAction) => (
    <div className="flex shrink-0 items-center gap-2">
      <Button
        variant="ghost"
        size="icon-xs"
        className="text-muted-foreground hover:text-foreground"
        onClick={() => onToggleTool(tool)}
        title={tool.is_enabled ? 'Deshabilitar' : 'Habilitar'}
      >
        {tool.is_enabled ? <PowerOff className="h-3.5 w-3.5" /> : <Power className="h-3.5 w-3.5" />}
      </Button>
      <Button variant="ghost" size="icon-xs" className="text-muted-foreground hover:text-foreground" onClick={() => onEditTool(tool)} title="Editar">
        <Edit className="h-3.5 w-3.5" />
      </Button>
      <Button
        variant="ghost"
        size="icon"
        className="text-destructive hover:bg-destructive/10 hover:text-destructive"
        onClick={() => onDeleteTool(tool)}
        title="Eliminar"
      >
        <Trash2 className="h-3.5 w-3.5" />
      </Button>
    </div>
  )

  const selectedCount = selectedToolIds.size + selectedFolderIds.size
  // Borrar en bloque se lleva las carpetas elegidas Y todo lo que cuelga de
  // ellas, y no se puede deshacer. Era un clic, sin preguntar.
  const [confirmarBorradoEnBloque, setConfirmarBorradoEnBloque] = React.useState(false)

  const collectDescendantFolderIds = React.useCallback((rootFolderId: string) => {
    const descendants: string[] = []
    const stack = [rootFolderId]
    while (stack.length) {
      const current = stack.pop() as string
      descendants.push(current)
      folders.forEach((folder) => {
        if (folder.parent_id === current) {
          stack.push(folder.id)
        }
      })
    }
    return descendants
  }, [folders])

  const handleDeleteSelected = async () => {
    const selectedTools = Array.from(selectedToolIds)
    const selectedFolders = Array.from(selectedFolderIds)
    if (!selectedTools.length && !selectedFolders.length) return

    const foldersToDelete = new Set<string>()
    selectedFolders.forEach((folderId) => {
      collectDescendantFolderIds(folderId).forEach((id) => foldersToDelete.add(id))
    })

    if (selectedTools.length) {
      if (onDeleteManyTools) {
        await onDeleteManyTools(selectedTools)
      } else if (selectedTools.length === 1) {
        const tool = tools.find((candidate) => candidate.id === selectedTools[0])
        if (tool) onDeleteTool(tool)
      }
    }

    if (onDeleteFolder && foldersToDelete.size > 0) {
      const ordered = Array.from(foldersToDelete).sort((a, b) => {
        const depth = (id: string) => {
          let d = 0
          let cursor: string | null = folderById.get(id)?.parent_id ?? null
          while (cursor) {
            d += 1
            cursor = folderById.get(cursor)?.parent_id ?? null
          }
          return d
        }
        return depth(b) - depth(a)
      })
      await Promise.all(ordered.map((folderId) => onDeleteFolder(folderId)))
    }

    clearSelection()
  }

  const handleBulkMovePickerChange = async (value: string) => {
    const targetFolderId = value === '__root__' ? null : value
    await moveSelectedItems(targetFolderId)
    setBulkMovePickerKey((prev) => prev + 1)
  }
  const shouldRenderToolbar = !(isMobile && hideMobileToolbar)

  return (
    <section
      ref={surfaceRef}
      className="relative min-h-[calc(100dvh-220px)] space-y-4"
      onMouseDown={handleRbMouseDown}
    >
      {shouldRenderToolbar && (isMobile ? (
        <div className="flex items-center gap-2" data-no-rubberband>
          {currentFolderId && (
            <Button
              type="button"
              variant="ghost"

              size="icon-sm"

              className="shrink-0"
              onClick={() => {
                const parentId = folderById.get(currentFolderId)?.parent_id ?? null
                setCurrentFolderId(parentId)
              }}
              title="Volver"
            >
              <ArrowLeft className="h-4 w-4" />
            </Button>
          )}
          <div className="relative min-w-0 flex-1">
            <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
            <Input
              placeholder="Nombre visible, interno o descripción"
              value={searchQuery}
              onChange={(e) => onSearchQueryChange(e.target.value)}
              className="h-9 pl-9"
            />
          </div>
          {onCreateTool && (
            <Button
              type="button"
              size="icon-sm"
              className="shrink-0"
              onClick={onCreateTool}
              aria-label="Crear Herramienta"
            >
              <Plus className="h-4 w-4" />
            </Button>
          )}
          <Button
            type="button"
            size="icon-sm"
            variant="outline"
            className="shrink-0"
            onClick={() => setShowCreateFolder((prev) => !prev)}
            aria-label="Nueva carpeta"
          >
            <FolderPlus className="h-4 w-4" />
          </Button>
          <div className="inline-flex shrink-0 items-center gap-1 rounded-md border bg-card p-1">
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className={cn(viewMode === 'grid' && 'bg-background')}
              onClick={() => setViewMode('grid')}
              title="Vista cards"
            >
              <LayoutGrid className="h-4 w-4" />
            </Button>
            <Button
              type="button"
              variant="ghost"
              size="icon-xs"
              className={cn(viewMode === 'list' && 'bg-background')}
              onClick={() => setViewMode('list')}
              title="Vista lista"
            >
              <List className="h-4 w-4" />
            </Button>
          </div>
        </div>
      ) : (
        <div className="flex flex-col gap-2 xl:flex-row xl:items-end xl:justify-between" data-no-rubberband>
          <div className="flex items-center gap-1 text-sm text-muted-foreground">
            {currentFolderId ? (
              <>
                <Button
                  type="button"
                  variant="ghost"
                  size="icon-xs"
                  onClick={() => {
                    const parentId = folderById.get(currentFolderId)?.parent_id ?? null
                    setCurrentFolderId(parentId)
                  }}
                  title="Volver"
                >
                  <ArrowLeft className="h-4 w-4" />
                </Button>
                {breadcrumbs.map((folder, idx) => (
                  <React.Fragment key={folder.id}>
                    {idx > 0 && <ChevronRight className="h-3.5 w-3.5" />}
                    <Button
                      variant="link"
                      className="text-muted-foreground hover:text-foreground hover:no-underline"
                      onClick={() => setCurrentFolderId(folder.id)}
                    >
                      {folder.name}
                    </Button>
                  </React.Fragment>
                ))}
              </>
            ) : (
              <div className="flex w-full flex-wrap items-end gap-2 xl:w-auto">
                <div className="relative w-full sm:w-[320px]">
                  <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                  <Input
                    placeholder="Nombre visible, interno o descripción"
                    value={searchQuery}
                    onChange={(e) => onSearchQueryChange(e.target.value)}
                    className="h-9 pl-9"
                  />
                </div>
                <Select value={typeFilter} onValueChange={onTypeFilterChange}>
                  <SelectTrigger className="h-9 w-[170px]">
                    <SelectValue placeholder="Tipo" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos los Tipos</SelectItem>
                    <SelectItem value="webhook">Webhook / HTTP Request</SelectItem>
                    <SelectItem value="mcp_server">MCP Server</SelectItem>
                  </SelectContent>
                </Select>
                <Select value={enabledFilter} onValueChange={onEnabledFilterChange}>
                  <SelectTrigger className="h-9 w-[140px]">
                    <SelectValue placeholder="Estado" />
                  </SelectTrigger>
                  <SelectContent>
                    <SelectItem value="all">Todos</SelectItem>
                    <SelectItem value="enabled">Habilitados</SelectItem>
                    <SelectItem value="disabled">Deshabilitados</SelectItem>
                  </SelectContent>
                </Select>
              </div>
            )}
          </div>
          <div className="flex items-center gap-2">
            <Button
              type="button"
              size="sm"
              variant="outline"
              onClick={() => setShowCreateFolder((prev) => !prev)}
            >
              <FolderPlus className="mr-1 h-4 w-4" />
              Nueva carpeta
            </Button>
            <div className="inline-flex items-center gap-1 rounded-md border bg-card p-1">
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className={cn(viewMode === 'grid' && 'bg-background')}
                onClick={() => setViewMode('grid')}
                title="Vista cards"
              >
                <LayoutGrid className="h-4 w-4" />
              </Button>
              <Button
                type="button"
                variant="ghost"
                size="icon-xs"
                className={cn(viewMode === 'list' && 'bg-background')}
                onClick={() => setViewMode('list')}
                title="Vista lista"
              >
                <List className="h-4 w-4" />
              </Button>
            </div>
          </div>
        </div>
      ))}

      {showCreateFolder && (
        <div className="flex items-center gap-2">
          <Input
            value={newFolderName}
            onChange={(event) => setNewFolderName(event.target.value)}
            placeholder="Nombre de carpeta"
          />
          <Button type="button" size="sm" onClick={handleCreateFolder} disabled={savingFolder}>
            {savingFolder ? 'Guardando...' : 'Crear'}
          </Button>
          <Button
            type="button"
            size="sm"
            variant="ghost"
            onClick={() => {
              setShowCreateFolder(false)
              setNewFolderName('')
            }}
          >
            Cancelar
          </Button>
        </div>
      )}

      {loading ? (
        <div className="space-y-2">
          {Array.from({ length: viewMode === 'grid' ? 6 : 8 }).map((_, index) => (
            <div key={`tool-content-skeleton-${index}`} className="rounded-lg border bg-card/80 p-4">
              <div className="flex items-start justify-between gap-2">
                <div className="min-w-0 flex flex-1 items-center gap-2">
                  <Skeleton className="h-8 w-8 rounded-lg" />
                  <div className="min-w-0 flex-1 space-y-2">
                    <Skeleton className="h-5 w-48" />
                    <Skeleton className="h-4 w-full" />
                  </div>
                </div>
                <div className="flex items-center gap-1">
                  <Skeleton className="h-8 w-8 rounded-md" />
                  <Skeleton className="h-8 w-8 rounded-md" />
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div
          className="relative space-y-2"
        >
          {viewMode === 'grid' ? (
            <div className="grid grid-cols-1 gap-2 lg:grid-cols-2 xl:grid-cols-3">
              {visibleFolders.map(renderFolderCard)}
              {visibleTools.map((tool) => {
                const isDragging = draggingToolIds.has(tool.id)
                const isSelected = selectedToolIds.has(tool.id)
                const mcpSummary = getMcpRemoteSummary(tool)
                return (
                  <article
                    key={tool.id}
                    draggable
                    onDragStart={(event) => handleDragStart(event, tool.id)}
                    onDragEnd={handleDragEnd}
                    onClick={(event) => handleToolCardClick(event, tool)}
                    data-tool-card
                    ref={(node) => {
                      if (node) toolCardRefs.current.set(tool.id, node)
                      else toolCardRefs.current.delete(tool.id)
                    }}
                    className={cn(
                      'rounded-lg border bg-card/80 p-4 transition-colors',
                      'hover:border-primary/40 hover:bg-card',
                      isDragging && 'opacity-50',
                      isSelected && 'border-primary'
                    )}
                  >
                    <div className="mb-3 flex items-start justify-between gap-2">
                      <div className="min-w-0 flex items-center gap-2">
                        <span className="inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-muted/40 text-foreground">
                          {toolIcons[tool.id]?.icon ? (
                            <StoredIcon
                              value={toolIcons[tool.id]?.icon}
                              colorClass={toolIcons[tool.id]?.colorClass}
                              className="h-4 w-4"
                            />
                          ) : (
                            <ExecutionTypeIcon type={tool.execution_type} className="h-4 w-4" />
                          )}
                        </span>
                        <h3 className="truncate text-base font-semibold">{tool.display_name}</h3>
                      </div>
                      <div
                        onMouseDown={(event) => event.stopPropagation()}
                        onClick={(event) => event.stopPropagation()}
                      >
                        {renderToolActions(tool)}
                      </div>
                    </div>

                    <div className="mb-3 flex flex-wrap items-center gap-2">
                      <Badge variant={tool.is_enabled ? 'default' : 'secondary'}>
                        {tool.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                      </Badge>
                      <ExecutionTypeChip type={tool.execution_type} />
                    </div>

                    <p className="mb-3 line-clamp-2 text-sm text-muted-foreground">
                      {tool.description || 'Sin descripción.'}
                    </p>
                    {mcpSummary && (
                      <p className="mb-2 text-xs text-muted-foreground">
                        Herramientas MCP activas: {mcpSummary.enabled}/{mcpSummary.total}
                      </p>
                    )}

                    <div className="mb-4 flex flex-wrap items-center gap-2 text-xs text-muted-foreground">
                      <span className="inline-flex items-center gap-1">
                        <Clock3 className="h-3.5 w-3.5 text-muted-foreground" />
                        Act. {formatRelativeTime(tool.updated_at)}
                      </span>
                    </div>
                  </article>
                )
              })}
            </div>
          ) : (
            <>
              {visibleFolders.map(renderFolderCard)}
              {visibleTools.map((tool) => {
                const isDragging = draggingToolIds.has(tool.id)
                const isSelected = selectedToolIds.has(tool.id)
                const mcpSummary = getMcpRemoteSummary(tool)
                return (
                  <article
                    key={tool.id}
                    draggable
                    onDragStart={(event) => handleDragStart(event, tool.id)}
                    onDragEnd={handleDragEnd}
                    onClick={(event) => handleToolCardClick(event, tool)}
                    data-tool-card
                    ref={(node) => {
                      if (node) toolCardRefs.current.set(tool.id, node)
                      else toolCardRefs.current.delete(tool.id)
                    }}
                    className={cn(
                      'rounded-lg border bg-card/80 p-3 transition-colors',
                      'hover:border-primary/40 hover:bg-card',
                      isDragging && 'opacity-50',
                      isSelected && 'border-primary'
                    )}
                  >
                    <div className="flex flex-col gap-2">
                      <div className="flex items-start justify-between gap-2">
                        <div className="flex min-w-0 flex-1 items-start gap-2">
                          <span className="mt-0.5 inline-flex h-8 w-8 items-center justify-center rounded-lg border bg-muted/40 text-foreground">
                            {toolIcons[tool.id]?.icon ? (
                              <StoredIcon
                                value={toolIcons[tool.id]?.icon}
                                colorClass={toolIcons[tool.id]?.colorClass}
                                className="h-4 w-4"
                              />
                            ) : (
                              <ExecutionTypeIcon type={tool.execution_type} className="h-4 w-4" />
                            )}
                          </span>
                          <div className="min-w-0">
                            <h3 className="truncate text-base font-semibold">{tool.display_name}</h3>
                            <p className="line-clamp-1 text-sm text-muted-foreground">
                              {tool.description || 'Sin descripción.'}
                            </p>
                            {mcpSummary && (
                              <p className="mt-0.5 text-xs text-muted-foreground">
                                Herramientas MCP activas: {mcpSummary.enabled}/{mcpSummary.total}
                              </p>
                            )}
                            <div className="mt-1 flex flex-wrap items-center gap-2">
                              <Badge variant={tool.is_enabled ? 'default' : 'secondary'}>
                                {tool.is_enabled ? 'Habilitado' : 'Deshabilitado'}
                              </Badge>
                              <ExecutionTypeChip type={tool.execution_type} />
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock3 className="h-3.5 w-3.5" />
                                Actualizado {formatRelativeTime(tool.updated_at)}
                              </span>
                              <span className="inline-flex items-center gap-1 text-xs text-muted-foreground">
                                <Clock3 className="h-3.5 w-3.5" />
                                Creado {formatRelativeTime(tool.created_at)}
                              </span>
                            </div>
                          </div>
                        </div>
                        <div
                          onMouseDown={(event) => event.stopPropagation()}
                          onClick={(event) => event.stopPropagation()}
                        >
                          {renderToolActions(tool)}
                        </div>
                      </div>
                    </div>
                  </article>
                )
              })}
            </>
          )}

          {rubberBand && surfaceRef.current && (
            <div
              className="pointer-events-none absolute z-20 rounded-md border-2 border-primary/50 bg-primary/10"
              style={{
                left: Math.min(rubberBand.startX, rubberBand.x) - surfaceRef.current.getBoundingClientRect().left,
                top: Math.min(rubberBand.startY, rubberBand.y) - surfaceRef.current.getBoundingClientRect().top,
                width: Math.abs(rubberBand.x - rubberBand.startX),
                height: Math.abs(rubberBand.y - rubberBand.startY),
              }}
            />
          )}

          {visibleFolders.length === 0 && visibleTools.length === 0 && (
            <div className="rounded-lg border border-dashed border-border bg-card/40 p-8 text-center text-sm text-muted-foreground">
              Esta carpeta está vacía.
            </div>
          )}
        </div>
      )}

      {selectedCount > 0 && (
        <div className="fixed bottom-[calc(1.5rem+env(safe-area-inset-bottom))] left-1/2 z-30 flex max-w-[calc(100vw-1rem)] flex-wrap -translate-x-1/2 items-center justify-center gap-2 rounded-lg border bg-background px-4 py-2.5 shadow-md">
          <span className="text-sm font-medium">{selectedCount} seleccionadas</span>
          <Select key={bulkMovePickerKey} onValueChange={(value) => void handleBulkMovePickerChange(value)} disabled={bulkMoving}>
            <SelectTrigger className="h-8 min-w-[170px] gap-1.5 text-xs">
              <span className="inline-flex items-center gap-1.5">
                <FolderInput className="h-3.5 w-3.5 text-muted-foreground" />
                {bulkMoving ? 'Moviendo...' : 'Mover a'}
              </span>
            </SelectTrigger>
            <SelectContent>
              <SelectItem value="__root__">Sin carpeta</SelectItem>
              {folders.length === 0 ? (
                <SelectItem value="__empty__" disabled>No hay carpetas</SelectItem>
              ) : (
                folders.map((folder) => (
                  <SelectItem key={folder.id} value={folder.id}>
                    {folder.name}
                  </SelectItem>
                ))
              )}
            </SelectContent>
          </Select>
          <Button
            variant="ghost"
            size="sm"
            className="gap-1.5 text-destructive hover:bg-destructive/10 hover:text-destructive"
            onClick={() => setConfirmarBorradoEnBloque(true)}
          >
            <Trash2 className="h-3.5 w-3.5" />
            Eliminar
          </Button>
          <div className="h-4 w-px bg-border/70" />
          <Button variant="ghost" size="xs" onClick={clearSelection}>
            <X className="mr-1 h-3.5 w-3.5" />
            Cerrar
          </Button>
        </div>
      )}
      {bulkMoveError && (
        <div className="fixed bottom-24 left-1/2 z-30 -translate-x-1/2 rounded-md border border-destructive/30 bg-destructive/10 px-3 py-1.5 text-xs text-destructive">
          {bulkMoveError}
        </div>
      )}

      <AlertDialog open={confirmarBorradoEnBloque} onOpenChange={setConfirmarBorradoEnBloque}>
        <AlertDialogContent>
          <AlertDialogHeader>
            <AlertDialogTitle>Eliminar lo seleccionado</AlertDialogTitle>
            <AlertDialogDescription>
              {/* Se dice cuantas carpetas y cuantas herramientas, por separado:
                  «7 elementos» no avisa de que entre ellos va una carpeta que se
                  lleva por delante todo lo que tiene dentro. */}
              {selectedFolderIds.size > 0 && (
                <>
                  Se eliminaran {selectedFolderIds.size}{' '}
                  {selectedFolderIds.size === 1 ? 'carpeta' : 'carpetas'}
                  <strong> con todo lo que contienen</strong>
                  {selectedToolIds.size > 0 && ', y '}
                </>
              )}
              {selectedToolIds.size > 0 && (
                <>
                  {selectedFolderIds.size === 0 && 'Se eliminaran '}
                  {selectedToolIds.size}{' '}
                  {selectedToolIds.size === 1 ? 'herramienta' : 'herramientas'}
                </>
              )}
              . No se puede deshacer.
            </AlertDialogDescription>
          </AlertDialogHeader>
          <AlertDialogFooter>
            <AlertDialogCancel>Cancelar</AlertDialogCancel>
            <AlertDialogAction
              onClick={() => {
                setConfirmarBorradoEnBloque(false)
                void handleDeleteSelected()
              }}
              className="bg-destructive text-destructive-foreground hover:bg-destructive/90"
            >
              Eliminar
            </AlertDialogAction>
          </AlertDialogFooter>
        </AlertDialogContent>
      </AlertDialog>
    </section>
  )
}
